/**
 * Table-of-contents rail for the bc-product-docs section.
 *
 * The element scopes its DOM queries to its enclosing [data-bc-docs]
 * container so it only indexes entries in the same docs panel. The sticky
 * release behavior is handled entirely in CSS (the rail's sticky containing
 * block is the grid row, which ends with the content column).
 *
 * Active-state tracking (which entry is highlighted) has two properties
 * worth knowing about:
 *
 *   1. It is GATED on the same breakpoint the rail's layout is. Above
 *      `data-min-viewport` the list is a sticky rail beside the content and
 *      "which section am I reading" is the whole point of it; below, it is a
 *      plain card sitting at the top of the panel, scrolled past within a
 *      screen or two — nothing to track. The observer therefore attaches and
 *      detaches with a matchMedia listener instead of running on phones.
 *      (Both the docs panel and the batch page emit the attribute from the
 *      shared theme setting `docs_toc_min_viewport` and interpolate the same
 *      number into their @media query, so JS and CSS can't disagree.
 *      MIN_VIEWPORT_FALLBACK is that setting's default, for an element
 *      rendered without the attribute.)
 *
 *   2. Tracking is driven by a SCROLL LISTENER, not by IntersectionObserver,
 *      and that is a correction rather than a preference. Horizon's desktop
 *      layout (≥990px, assets/base.css) puts `overflow: hidden` on html/body
 *      and scrolls a `.page-wrapper` div instead — so `.page-wrapper` is an
 *      ancestor CLIP container sitting between every heading and the viewport.
 *      IntersectionObserver clips a target's rect against each such ancestor
 *      BEFORE intersecting it with the root, and `rootMargin` expands the ROOT,
 *      never undoes the target's clip. A heading scrolled above the wrapper's
 *      top edge is therefore clipped away to nothing and reports
 *      `isIntersecting: false` however far the root is stretched upwards.
 *      That is what made the previous scheme (an observer whose root was
 *      "everything above the activation line", 100000px of top margin and all)
 *      light an entry only while its heading was still on screen, and leave the
 *      whole body of a long section with NOTHING highlighted. Measured in
 *      Chrome, heading 1400px above the wrapper's top edge, identical margins:
 *      root null → isIntersecting false; root `.page-wrapper` → true.
 *
 *      So: one passive, rAF-throttled scroll listener that re-measures the
 *      headings and lights the last one whose top has crossed the line. Entry
 *      counts are small (~10-30) and a scroll-only frame has clean layout, so
 *      that rect pass is a batch of cheap reads, not a forced reflow.
 *
 *      The listener is registered on `document` in the CAPTURE phase, and that
 *      is load-bearing: scroll events do not bubble, so a bubble-phase listener
 *      on window/document hears the document scrolling (<990px) and is deaf to
 *      `.page-wrapper` (≥990px) — deaf, that is, across most of the range where
 *      this rail is shown at all. Capture reaches both, plus any other scroller
 *      Horizon may introduce, without this file naming `.page-wrapper` or
 *      hardcoding 990px. It also hears unrelated inner scrollers (a wide table,
 *      a chart panning); harmless — a recompute just re-derives the same
 *      answer. See #attachTracking / #recomputeActive.
 *
 *      Layout that does NOT move per frame is still read once per attach: the
 *      activation line (#headerOffset — a getComputedStyle) stays cached, and
 *      is re-placed on the header's ResizeObserver and on a settled resize.
 */

// The deepest level the nav styles define an indent step for
// (.bc-product-docs__nav-item--h4, in the stylesheet of
// sections/bc-product-docs.liquid). A deeper level would get a modifier class
// no rule matches and render flush against the top-level entries — reading as
// one of them — so levels below this collapse onto it. Raise both together.
const MAX_NAV_LEVEL = 4;
// Breakpoint used when the element carries no data-min-viewport: the default
// of the shared theme setting `docs_toc_min_viewport` (Theme settings → BC
// Docs table of contents), which both rails read.
const MIN_VIEWPORT_FALLBACK = 900;
// Distance below the sticky header at which a heading becomes "current".
// Same place a clicked link lands (see #onClick's scroll-margin-top).
const ACTIVATION_OFFSET_PX = 88;
// Window between the header resize settling and re-placing the activation
// line (a getComputedStyle read, which is why it is cached rather than taken
// per scroll frame).
const RESIZE_DEBOUNCE_MS = 150;

class BcProductToc extends HTMLElement {
  connectedCallback() {
    if (this.#initialized) return;
    this.#initialized = true;
    this.#listEl = this.querySelector('[data-toc-list]');
    this.#root =
      this.closest('[data-bc-docs]') ||
      this.closest('.content-for-layout') ||
      document.querySelector('main') ||
      document;
    if (!this.#listEl) return;

    // Mirror our color-scheme class onto the section wrapper so the
    // scheme's background/foreground fill the entire ToC column (the
    // stretched grid cell), not just the sticky nav inside it.
    const colorClass = [...this.classList].find((c) => c.startsWith('color-'));
    const sectionWrap = this.closest('.bc-product-toc-section');
    if (colorClass && sectionWrap && !sectionWrap.classList.contains(colorClass)) {
      sectionWrap.classList.add(colorClass);
    }

    // Stored as bound refs so disconnectedCallback can remove them.
    // Arrow-function listeners (the previous form) couldn't be removed
    // and would accumulate every time a section reload reconnected this
    // element, holding references to the now-detached entries.
    this.#boundOnSectionLoad = () => this.#build();
    document.addEventListener('shopify:section:load', this.#boundOnSectionLoad);

    // See the header comment: tracking only exists where the rail does.
    const min = parseInt(this.dataset.minViewport, 10);
    this.#mediaQuery = window.matchMedia(
      `(min-width: ${Number.isFinite(min) && min > 0 ? min : MIN_VIEWPORT_FALLBACK}px)`,
    );
    this.#boundOnMediaChange = () => this.#syncTracking();
    this.#mediaQuery.addEventListener('change', this.#boundOnMediaChange);

    // The activation line is cached per attach, and the only thing that moves
    // it is the header changing height. Watch the header for that (header.js
    // republishes --header-*-height from its own ResizeObserver, so this is
    // the same signal) instead of re-reading getComputedStyle(document.body)
    // per scroll frame. Debounced so a resize drag doesn't re-place it dozens
    // of times.
    // The whole header GROUP (announcement bar + utility bar + header), since
    // --header-group-height is what #headerOffset prefers and any member can
    // change height on its own; header-component is the fallback.
    const header = document.querySelector('#header-group') || document.querySelector('header-component');
    if (header && window.ResizeObserver) {
      this.#headerObserver = new ResizeObserver(this.#boundScheduleReattach);
      this.#headerObserver.observe(header);
    }
    // A viewport resize (orientation change, devtools) moves the header and
    // reflows every heading, so re-place the line and recompute once it
    // settles.
    window.addEventListener('resize', this.#boundScheduleReattach, { passive: true });

    this.#build();
  }

  disconnectedCallback() {
    if (this.#boundOnSectionLoad) {
      document.removeEventListener('shopify:section:load', this.#boundOnSectionLoad);
      this.#boundOnSectionLoad = null;
    }
    if (this.#boundOnMediaChange) {
      this.#mediaQuery?.removeEventListener('change', this.#boundOnMediaChange);
      this.#boundOnMediaChange = null;
    }
    if (this.#headerObserver) {
      this.#headerObserver.disconnect();
      this.#headerObserver = null;
    }
    window.removeEventListener('resize', this.#boundScheduleReattach);
    clearTimeout(this.#resizeTimer);
    this.#resizeTimer = 0;
    this.#detachTracking();
    this.#clearScrollLock();
    this.#mediaQuery = null;
    this.#initialized = false;
  }

  #initialized = false;
  #listEl = null;
  #root = null;
  #entries = [];
  #scrollTargetId = null;
  #scrollLockTimer = 0;
  #boundOnSectionLoad = null;
  #mediaQuery = null;
  #boundOnMediaChange = null;
  #headerObserver = null;
  #resizeTimer = 0;
  // Whether the scroll listener is currently attached (i.e. we are above the
  // breakpoint with entries to track).
  #tracking = false;
  #rafId = 0;
  #boundScheduleReattach = () => {
    if (!this.#tracking) return; // not tracking — nothing to re-place
    clearTimeout(this.#resizeTimer);
    this.#resizeTimer = setTimeout(() => this.#syncTracking(), RESIZE_DEBOUNCE_MS);
  };
  // Scroll fires far faster than the rail can meaningfully change, so coalesce
  // to one recompute per frame. Reading rects inside rAF also keeps the reads
  // batched with the browser's own pre-paint work instead of interleaving them
  // with style writes.
  #boundOnScroll = () => {
    if (this.#rafId) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = 0;
      this.#recomputeActive();
    });
  };
  // Viewport y of the "current heading" line, cached per attach.
  #activationLine = 0;
  #activeId = null;

  #build() {
    // Gather, in document order, section markers ([data-toc-entry]), any
    // in-content headings up to h3, and anything that opts in with
    // [data-toc-item] — skipping anything inside the ToC itself or under
    // [data-toc-skip]. When markers exist they form the flush top-level
    // entries and headings authored inside a section's prose (e.g. an h3 in a
    // doc_section's rich text) nest beneath it. With no markers anywhere we
    // fall back to a pure h1/h2/h3 scan (h1 at the top of the rail, typically
    // the page title; h2/h3 indented under it).
    //
    // [data-toc-item] IS THE OPT-IN for a heading the tag scan does not reach
    // — an h4 or deeper. Opt-in rather than a wider scan, because pages carry
    // plenty of h4s that are details INSIDE a section rather than landmarks of
    // the page (the PDP docs panel is full of them, and nothing there opts in),
    // and indexing them wholesale would bury the structure the rail exists to
    // show. An opted-in node is indexed at its own heading level, so an h4
    // nests one step under the h3 above it, and [data-toc-skip] filters it
    // exactly as it filters everything else.
    //
    // A node matching SEVERAL of these selectors (a future h3[data-toc-item],
    // say) still produces ONE entry: querySelectorAll returns each matching
    // element once, in document order, however many selectors of the list it
    // satisfies. Nothing here has to deduplicate.
    const nodes = Array.from(
      this.#root.querySelectorAll('[data-toc-entry], [data-toc-item], h1, h2, h3'),
    ).filter((node) => !this.contains(node) && !node.closest('[data-toc-skip]'));

    this.#listEl.innerHTML = '';
    this.#entries = [];
    // The links that carried the active class are gone with the list, and a
    // rebuild can mint the same ids again — clear the memo or #setActive
    // would short-circuit and never light the NEW link.
    this.#activeId = null;
    // Stop tracking the previous entries here rather than only in the
    // re-attach below, so the early returns below can't leave a live scroll
    // listener recomputing against headings that are no longer in the rail.
    this.#detachTracking();
    if (nodes.length === 0) return;

    const hasMarkers = nodes.some((node) => node.hasAttribute('data-toc-entry'));

    // Resolve each node to an entry descriptor { el, level, label }. Level 1 is
    // the flush top level; 2/3/4 indent via the --h2/--h3/--h4 nav-item
    // modifiers.
    //
    // `number` is OPT-IN via [data-toc-number] (the product manual stamps it on
    // its chapter markers and numbered sub-headings, computed once in Liquid):
    // the rail shows the SAME string the page prints, never a count of its own.
    // Absent everywhere else, so other rails render exactly as before.
    const items = [];
    for (const node of nodes) {
      const number = (node.dataset.tocNumber || '').trim();
      if (hasMarkers && node.hasAttribute('data-toc-entry')) {
        // Top-level section entry. Markers always carry data-toc-label — never
        // fall back to a <section>'s textContent (that's the whole section).
        items.push({ el: node, level: 1, number, label: (node.dataset.tocLabel || node.id || '').trim() });
      } else if (hasMarkers) {
        // In-content heading. Skip a section's own title heading — it carries
        // the bc-product-docs__heading class and duplicates the marker entry
        // right above it. Authored h2/h3 in the prose nest beneath the section
        // at their natural heading level.
        if (node.classList.contains('bc-product-docs__heading')) continue;
        items.push({ el: node, level: this.#levelOf(node), number, label: this.#labelOf(node) });
      } else {
        items.push({ el: node, level: this.#levelOf(node), number, label: this.#labelOf(node) });
      }
    }
    if (items.length === 0) return;

    // Pad above each target so clicks (and direct #hash navigation) land
    // below the sticky header instead of behind it. Use a live calc()
    // referencing --header-height (published on <body> by header.js, only
    // the main header actually stays sticky) so the offset stays correct
    // when the header resizes after scroll. The clearance below the
    // header was user-tuned in conversation; bump here to retune.
    const HEADER_CLEARANCE_PX = 27;
    const scrollMarginTop = `calc(var(--header-height, 60px) + ${HEADER_CLEARANCE_PX}px)`;

    // Per-instance prefix so two <bc-product-toc> on the same page don't
    // both assign toc-target-0 to different nodes.
    const idPrefix = this.#instanceId();
    const frag = document.createDocumentFragment();

    items.forEach((item, i) => {
      const el = item.el;
      el.style.scrollMarginTop = scrollMarginTop;
      if (!el.id) el.id = `${idPrefix}-${i}`;
      const li = document.createElement('li');
      li.className = `bc-product-docs__nav-item bc-product-docs__nav-item--h${item.level}`;
      const a = document.createElement('a');
      a.className = 'bc-product-docs__nav-link';
      a.href = `#${el.id}`;
      a.textContent = item.label || el.id;
      if (item.number) {
        // Number first, as its own span (tabular gutter, muted — styled by the
        // page that opts in), then a space so the link's accessible name reads
        // "3 Nomenclature".
        a.classList.add('bc-product-docs__nav-link--num');
        const num = document.createElement('span');
        num.className = 'bc-product-docs__nav-num';
        num.textContent = item.number;
        a.prepend(num, ' ');
      }
      a.addEventListener('click', (e) => this.#onClick(e, el.id));
      li.appendChild(a);
      frag.appendChild(li);
      this.#entries.push({ id: el.id, el, link: a });
    });

    this.#listEl.appendChild(frag);

    this.#syncTracking();
  }

  // An in-content heading's rail label: data-toc-label, else its text WITHOUT
  // a printed number ([data-toc-num]) — the number is rendered separately.
  #labelOf(node) {
    if (node.dataset.tocLabel) return node.dataset.tocLabel.trim();
    let text = node.textContent || '';
    const num = node.querySelector('[data-toc-num]');
    if (num) {
      const clone = node.cloneNode(true);
      clone.querySelectorAll('[data-toc-num]').forEach((n) => n.remove());
      text = clone.textContent || '';
    }
    return (text || node.id || '').trim();
  }

  // The level a non-marker node is indexed at: its own heading tag (h3 → 3),
  // falling back to 2 for anything that is not a heading at all — only
  // reachable through [data-toc-item], which any element may carry. Clamped to
  // MAX_NAV_LEVEL so an opted-in h5/h6 indents to the deepest step the nav
  // styles actually define instead of styling as a top-level entry.
  #levelOf(node) {
    const level = parseInt(node.tagName.slice(1), 10) || 2;
    return Math.min(level, MAX_NAV_LEVEL);
  }

  #onClick(e, id) {
    const target = document.getElementById(id);
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (history.replaceState) history.replaceState(null, '', `#${id}`);
    this.#setActive(id);
    // Below the breakpoint nothing tracks scrolling, so the click's own
    // highlight is the final word — no lock to arm.
    if (!this.#tracking) return;
    // Lock the active link on the click target until the smooth scroll
    // actually lands on it (its top crosses the activation line), with a 2s
    // upper bound in case the scroll is interrupted or misses. The timer is
    // the bound: a scroll that is cancelled before it arrives stops producing
    // scroll events, so nothing else would ever release the lock.
    this.#scrollTargetId = id;
    clearTimeout(this.#scrollLockTimer);
    this.#scrollLockTimer = setTimeout(() => {
      this.#scrollTargetId = null;
      this.#scrollLockTimer = 0;
      this.#recomputeActive();
    }, 2000);
  }

  #clearScrollLock() {
    this.#scrollTargetId = null;
    clearTimeout(this.#scrollLockTimer);
    this.#scrollLockTimer = 0;
  }

  #setActive(activeId) {
    if (activeId === this.#activeId) return;
    this.#activeId = activeId;
    for (const entry of this.#entries) {
      if (!entry.link) continue;
      entry.link.classList.toggle('is-active', entry.id === activeId);
      if (entry.id === activeId) {
        entry.link.setAttribute('aria-current', 'true');
        // bc: keep the active link visible inside an overflowing (scrollable) rail.
        this.#revealInRail(entry.link);
      } else {
        entry.link.removeAttribute('aria-current');
      }
    }
  }

  // Scrolls ONLY the rail's own scroll container (never the page): manual
  // scrollTop math, no-op when nothing overflows (PDP / batch rails).
  #revealInRail(link) {
    // The rail's scroll box is either this element (the PDP docs panel caps
    // and scrolls <bc-product-toc> itself) or its direct parent (the batch
    // report and manual cap the <aside>). Never walk further: Horizon's
    // .page-wrapper is the page's own scroller (overflow-y: auto at ≥990px)
    // and would otherwise be picked whenever the rail does not overflow,
    // turning this into a page scroll that re-fires the scrollspy.
    const isScrollBox = (el) =>
      el && el.scrollHeight > el.clientHeight + 1 && /(auto|scroll)/.test(getComputedStyle(el).overflowY);
    let box = null;
    if (isScrollBox(this)) box = this;
    else if (isScrollBox(this.parentElement)) box = this.parentElement;
    if (!box || box.classList.contains('page-wrapper') || box === document.scrollingElement) return;
    const b = box.getBoundingClientRect();
    const l = link.getBoundingClientRect();
    // Only move when the entry is near or past an edge, and then bring it to
    // the rail's middle: a link parked flush at the bottom edge is easy to
    // lose (and sits under any bottom bar), and centring means the entries
    // around it stay readable as the reader keeps scrolling.
    const pad = 24;
    const outside = l.top < b.top + pad || l.bottom > b.bottom - pad;
    if (!outside) return;
    const delta = (l.top + l.height / 2) - (b.top + b.height / 2);
    if (!delta) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    box.scrollTo({ top: box.scrollTop + delta, behavior: reduce ? 'auto' : 'smooth' });
  }

  #syncTracking() {
    if (this.#mediaQuery?.matches) this.#attachTracking();
    else this.#detachTracking();
  }

  /**
   * Start tracking, and also the refresh path: re-place the cached activation
   * line (header height changed / viewport resized) and recompute. Idempotent
   * — re-attaching while already tracking keeps the one listener and does not
   * blank the rail on the way through.
   */
  #attachTracking() {
    // Placed here rather than per frame: this is a getComputedStyle read, and
    // the header only relayouts on the signals that call us (see the header
    // comment and #headerOffset).
    this.#activationLine = this.#headerOffset() + ACTIVATION_OFFSET_PX;

    if (!this.#entries.length) {
      this.#detachTracking();
      return;
    }

    if (!this.#tracking) {
      this.#tracking = true;
      // CAPTURE phase, deliberately — scroll events do not bubble, so this is
      // the only registration on document that hears Horizon's `.page-wrapper`
      // scroller (desktop) as well as the document one (mobile). See the
      // header comment; getting this wrong is silent, not noisy.
      document.addEventListener('scroll', this.#boundOnScroll, {
        capture: true,
        passive: true,
      });
    }

    // Paint with the right entry lit straight away rather than waiting for the
    // reader's first scroll.
    this.#recomputeActive();
  }

  #detachTracking() {
    if (this.#tracking) {
      document.removeEventListener('scroll', this.#boundOnScroll, { capture: true });
      this.#tracking = false;
    }
    if (this.#rafId) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = 0;
    }
    this.#setActive(null);
  }

  /**
   * The single place the rail's state is derived. Measures each heading and
   * lights the LAST one whose top has crossed the activation line — the
   * section the reader is inside, whether or not its heading is still on
   * screen. That "whether or not" is the entire point: the observer scheme
   * this replaced could only see headings that were still painted.
   *
   * Before anything has crossed (the reader is above the first heading) the
   * first entry lights as soon as it is on screen, so the rail is never blank
   * while indexed content is visible. Above that — the whole indexed region
   * still below the fold — nothing lights, which is also the state in which
   * the rail itself has not been reached.
   */
  #recomputeActive() {
    if (!this.#entries.length) return;

    const line = this.#activationLine;

    // If a click-to-scroll is in flight, hold the active link on the target
    // until the scroll actually reaches it (or the lock times out). This is an
    // explicit "has it arrived" test rather than the last-crossed rule below,
    // because the click lands the target at its scroll-margin-top, which can
    // sit a little ABOVE or BELOW the activation line depending on the
    // header-group height.
    if (this.#scrollTargetId) {
      const target = this.#entries.find((e) => e.id === this.#scrollTargetId);
      if (target && target.el.getBoundingClientRect().top > line + 1) return;
      this.#clearScrollLock();
    }

    // Entries are in document order, so the last one whose top has crossed the
    // line is the section the reader is in. Scanned in full rather than broken
    // out of at the first uncrossed heading: document order does not strictly
    // guarantee increasing rect tops (a floated or grid-placed heading can sit
    // out of sequence), and the list is short enough that it costs nothing.
    const viewportBottom = window.innerHeight;
    let activeId = null;
    let firstTop = 0;
    this.#entries.forEach((entry, i) => {
      const top = entry.el.getBoundingClientRect().top;
      if (i === 0) firstTop = top;
      if (top <= line) activeId = entry.id;
    });
    if (!activeId && firstTop < viewportBottom) activeId = this.#entries[0].id;

    this.#setActive(activeId);
  }

  #instanceId() {
    if (this.#cachedInstanceId) return this.#cachedInstanceId;
    // Prefer the section's stable id when available; otherwise mint a
    // random short token. Cached so repeated #build() calls are stable.
    const wrap = this.closest('[id^="shopify-section-"]');
    const fromSection = wrap && wrap.id ? wrap.id.replace(/[^a-z0-9]+/gi, '-').slice(-12) : '';
    this.#cachedInstanceId = 'toc-' + (fromSection || Math.random().toString(36).slice(2, 8));
    return this.#cachedInstanceId;
  }

  #cachedInstanceId = '';

  #headerOffset() {
    // --header-group-height (full header group) and --header-height (just
    // the main sticky row) are both published on <body> by header.js.
    // Prefer the full group height for "below the header" calculations.
    // Called once per attach (build / breakpoint change / settled resize) —
    // header.js only republishes these when the header relayouts, so a
    // getComputedStyle here is not something to pay for per scroll frame.
    const styles = getComputedStyle(document.body);
    const v =
      styles.getPropertyValue('--header-group-height').trim() ||
      styles.getPropertyValue('--header-height').trim();
    if (!v) return 0;
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  }
}

customElements.define('bc-product-toc', BcProductToc);
